import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FaEnvelope, FaMapMarkerAlt, FaPhone, FaPaperPlane, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { io } from 'socket.io-client';
import './Contact.css';
import { API_BASE_URL } from '../config';

const SOCKET_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5002';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const socketRef = useRef(null);
  const typingTimerRef = useRef(null);

  // ── Socket.io connection ─────────────────────────────────────────────────────
  useEffect(() => {
    socketRef.current = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    return () => {
      socketRef.current?.disconnect();
    };
  }, []);

  const emitTyping = useCallback((field, value) => {
    if (!socketRef.current) return;
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      socketRef.current.emit('contact:typing', {
        field,
        name: field === 'name' ? value : formData.name,
        subject: field === 'subject' ? value : formData.subject
      });
    }, 400); // debounce 400ms
  }, [formData.name, formData.subject]);

  // Emit a full preview when the visitor leaves (blurs) any field
  const handleBlur = (e) => {
    const updated = { ...formData, [e.target.name]: e.target.value };
    if (socketRef.current && (updated.name || updated.email)) {
      socketRef.current.emit('contact:preview', updated);
    }
  };

  // ── Contact info ─────────────────────────────────────────────────────────────
  const contactInfo = [
    {
      icon: FaEnvelope,
      title: 'Email',
      value: 'lakshmanan.r.dev@gmail.com',
      link: 'mailto:lakshmanan.r.dev@gmail.com'
    },
    {
      icon: FaMapMarkerAlt,
      title: 'Location',
      value: 'Chennai, Tamil Nadu, India',
      link: null
    },
    {
      icon: FaPhone,
      title: 'Phone',
      value: '+91 91763 54570',
      link: 'tel:+919176354570'
    }
  ];

  // ── Form handlers ─────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    emitTyping(name, value);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        // auto-clear success after 6 s
        setTimeout(() => setSubmitStatus(null), 6000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <section className="contact section" id="contact">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Contact</h2>
          <p className="section-subtitle">Get in touch with me</p>
        </motion.div>

        <div className="contact-content">
          {/* ── Left: info ──────────────────────────────── */}
          <motion.div
            className="contact-info"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3>Let's talk about everything!</h3>
            <p>
              Don't like forms? Send me an email directly — I'll get back to you as soon as possible.
            </p>

            <div className="contact-items">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={index}
                  className="contact-item"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="contact-icon"><item.icon /></div>
                  <div className="contact-details">
                    <h4>{item.title}</h4>
                    {item.link
                      ? <a href={item.link} target="_blank" rel="noopener noreferrer">{item.value}</a>
                      : <span>{item.value}</span>
                    }
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* ── Right: form ─────────────────────────────── */}
          <motion.div
            className="contact-form-container"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={errors.name ? 'error' : ''}
                    autoComplete="name"
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>
                <div className="form-group">
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={errors.email ? 'error' : ''}
                    autoComplete="email"
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
              </div>

              <div className="form-group">
                <input
                  type="text"
                  name="subject"
                  placeholder="Subject"
                  value={formData.subject}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.subject ? 'error' : ''}
                />
                {errors.subject && <span className="error-message">{errors.subject}</span>}
              </div>

              <div className="form-group">
                <textarea
                  name="message"
                  placeholder="Your Message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.message ? 'error' : ''}
                />
                {errors.message && <span className="error-message">{errors.message}</span>}
              </div>

              <button type="submit" className="btn submit-btn" disabled={isSubmitting}>
                {isSubmitting ? (
                  <><span className="spinner" /> Sending…</>
                ) : (
                  <><FaPaperPlane /> Send Message</>
                )}
              </button>

              {submitStatus && (
                <motion.div
                  className={`submit-status ${submitStatus}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {submitStatus === 'success' ? (
                    <><FaCheckCircle /> Message sent! I'll get back to you soon.</>
                  ) : (
                    <><FaTimesCircle /> Something went wrong — please try again or email me directly.</>
                  )}
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;